import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { BackgroundTask } from './types';
import { getTaskStatusIcon, getTaskStatusColor, getTaskStatusLabel, getTaskStatusBorderBg } from './helpers';

interface BackgroundTaskCardProps {
  task: BackgroundTask;
  onDismiss?: (taskId: string) => void;
  onRetry?: (taskId: string) => void;
  onViewDetails?: (taskId: string) => void;
}

export const BackgroundTaskCard: React.FC<BackgroundTaskCardProps> = ({
  task,
  onDismiss,
  onRetry,
  onViewDetails,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);

  const formatEstimatedTime = (seconds?: number) => {
    if (!seconds) return '预计时间未知';
    if (seconds < 60) return `预计 ${seconds} 秒`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `预计 ${minutes} 分 ${remainingSeconds} 秒`;
  };

  const handleDismiss = () => {
    setIsDismissing(true);
    setTimeout(() => {
      onDismiss?.(task.taskId);
    }, 300);
  };

  useEffect(() => {
    if (task.status === 'completed') {
      const timer = setTimeout(() => {
        handleDismiss();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [task.status]);

  return (
    <div
      className={`
        relative border-2 border-dashed rounded-lg p-4 transition-all duration-300 cursor-pointer
        ${getTaskStatusBorderBg(task.status)}
        ${isDismissing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}
        ${isExpanded ? 'shadow-lg' : 'hover:shadow-md'}
      `}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <span className={`text-base ${getTaskStatusColor(task.status)}`}>
            {getTaskStatusIcon(task.status)}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-gray-200 truncate">
                {task.taskName}
              </h4>
              <div className="flex items-center gap-2 mt-1">
                <span className={`
                  inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs
                  ${task.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' :
                    task.status === 'failed' ? 'bg-red-500/10 text-red-400' :
                    task.status === 'running' ? 'bg-yellow-500/10 text-yellow-400' :
                    'bg-blue-500/10 text-blue-400'}
                `}>
                  {getTaskStatusLabel(task.status)}
                </span>
                {(task.status === 'searching' || task.status === 'running') && task.estimatedTime && (
                  <span className="text-xs text-gray-500">
                    {formatEstimatedTime(task.estimatedTime)}
                  </span>
                )}
              </div>
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="flex-shrink-0 p-1 text-gray-500 hover:text-gray-400 transition-colors"
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>

          {isExpanded && (
            <div className="mt-3 pt-3 border-t border-gray-700/50 space-y-2">
              <div className="text-xs text-gray-400">
                <span className="text-gray-500">任务ID:</span> {task.taskId}
              </div>
              {task.agentId && (
                <div className="text-xs text-gray-400">
                  <span className="text-gray-500">智能体ID:</span> {task.agentId}
                </div>
              )}

              <div className="flex items-center gap-2 pt-2">
                {task.status === 'failed' && onRetry && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRetry(task.taskId);
                    }}
                    className="px-3 py-1.5 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded transition-colors"
                  >
                    重试
                  </button>
                )}
                {onViewDetails && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewDetails(task.taskId);
                    }}
                    className="px-3 py-1.5 text-xs bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded transition-colors"
                  >
                    查看详情
                  </button>
                )}
                {(task.status === 'completed' || task.status === 'failed') && onDismiss && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDismiss();
                    }}
                    className="px-3 py-1.5 text-xs bg-gray-500/10 hover:bg-gray-500/20 text-gray-400 rounded transition-colors"
                  >
                    关闭
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {task.status === 'completed' && (
        <div className="absolute top-2 right-2">
          <div className="flex items-center gap-1 text-xs text-emerald-500 opacity-0 animate-pulse">
            ✅
            <span>即将关闭</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackgroundTaskCard;
